import { apiClient } from './api-client';

// ── Enums ─────────────────────────────────────────────────────────────────────

export type ProcedureStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export type ProcedureType = 'INSPECTION' | 'SERVICE';
export type MaterialType = 'IMAGE' | 'VIDEO' | 'DOCUMENT';

// ── Shared shapes ─────────────────────────────────────────────────────────────

export interface UserRef {
  id: string;
  fullName: string;
}

export interface ModelRef {
  id: string;
  name: string | null;
  modelNumber: string;
  manufacturer: { id: string; name: string };
  equipmentTypeName: string;
}

export interface ReferenceMaterial {
  id: string;
  materialType: MaterialType;
  fileName: string;
  fileSize: number;
  mimeType: string;
  signedUrl: string;
}

// ── Template shapes ───────────────────────────────────────────────────────────

export interface ProcedureStep {
  id: string;
  title: string;
  description: string | null;
  instructionContent: string | null;
  sortOrder: number;
  isMandatory: boolean;
  passFailRequired: boolean;
  authorizationRequired: boolean;
  evidenceRequired: boolean;
  commentsAllowed: boolean;
  minPhotos: number;
  minVideos: number;
  minDocuments: number;
  referenceMaterials: ReferenceMaterial[];
}

export interface ProcedureSection {
  id: string;
  title: string;
  sortOrder: number;
  steps: ProcedureStep[];
}

export interface ProcedureTemplate {
  id: string;
  familyId: string;
  previousVersionId: string | null;
  versionNumber: number;
  title: string;
  description: string | null;
  procedureType: ProcedureType;
  status: ProcedureStatus;
  customerVisible: boolean;
  /** Procedure-level policy: mandatory procedures gate report generation. */
  isMandatory: boolean;
  equipmentModel: ModelRef | null;
  equipmentModelId: string | null;
  publishedAt: string | null;
  publishedBy: UserRef | null;
  archivedAt: string | null;
  archivedBy: UserRef | null;
  createdBy: UserRef;
  createdAt: string;
  updatedAt: string;
  sections: ProcedureSection[];
}

export interface ProcedureTemplateListItem {
  id: string;
  familyId: string;
  versionNumber: number;
  title: string;
  description: string | null;
  procedureType: ProcedureType;
  status: ProcedureStatus;
  customerVisible: boolean;
  /** Procedure-level policy: mandatory procedures gate report generation. */
  isMandatory: boolean;
  equipmentModel: ModelRef | null;
  publishedAt: string | null;
  publishedBy: UserRef | null;
  createdBy: UserRef;
  createdAt: string;
  totalSections: number;
  totalSteps: number;
  /** Present on DRAFT rows when a previous published version of the same family is still active for auto-attach. */
  activePublishedVersion: { id: string; versionNumber: number } | null;
}

export interface ProcedureVersion {
  id: string;
  versionNumber: number;
  status: ProcedureStatus;
  createdBy: UserRef;
  createdAt: string;
  publishedAt: string | null;
  publishedBy?: UserRef;
  archivedAt?: string | null;
}

export interface ProcedureListResponse {
  data: ProcedureTemplateListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ── DTOs ─────────────────────────────────────────────────────────────────────

export interface ProcedureListFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: ProcedureStatus | '';
  procedureType?: ProcedureType | '';
  equipmentModelId?: string;
  versionScope?: 'CURRENT_ONLY' | 'ALL_VERSIONS';
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CreateProcedureDto {
  title: string;
  description?: string;
  procedureType: ProcedureType;
  equipmentModelId: string;
  customerVisible?: boolean;
  isMandatory?: boolean;
}

export interface UpdateProcedureDto {
  title?: string;
  description?: string;
  equipmentModelId?: string;
  customerVisible?: boolean;
  isMandatory?: boolean;
}

export interface CloneProcedureDto {
  title: string;
  equipmentModelId: string;
  procedureType: ProcedureType;
}

export interface CreateSectionDto {
  title: string;
  sortOrder?: number;
}

export interface CreateStepDto {
  title: string;
  description?: string;
  instructionContent?: string;
  sortOrder?: number;
  isMandatory?: boolean;
  passFailRequired?: boolean;
  authorizationRequired?: boolean;
  evidenceRequired?: boolean;
  commentsAllowed?: boolean;
  minPhotos?: number;
  minVideos?: number;
  minDocuments?: number;
}

export type UpdateStepDto = Partial<CreateStepDto>;

// ── Service ───────────────────────────────────────────────────────────────────

export const procedureTemplateService = {
  // Template CRUD
  list: (filters: ProcedureListFilters = {}): Promise<ProcedureListResponse> => {
    const params: Record<string, string | number | undefined> = {};
    if (filters.page) params.page = filters.page;
    if (filters.pageSize) params.pageSize = filters.pageSize;
    if (filters.search) params.search = filters.search;
    if (filters.status) params.status = filters.status;
    if (filters.procedureType) params.procedureType = filters.procedureType;
    if (filters.equipmentModelId) params.equipmentModelId = filters.equipmentModelId;
    if (filters.versionScope) params.versionScope = filters.versionScope;
    if (filters.sortBy) params.sortBy = filters.sortBy;
    if (filters.sortOrder) params.sortOrder = filters.sortOrder;
    return apiClient
      .get<ProcedureListResponse>('/procedure-templates', { params })
      .then((r) => r.data);
  },

  get: (id: string): Promise<ProcedureTemplate> =>
    apiClient.get<ProcedureTemplate>(`/procedure-templates/${id}`).then((r) => r.data),

  create: (dto: CreateProcedureDto): Promise<ProcedureTemplate> =>
    apiClient.post<ProcedureTemplate>('/procedure-templates', dto).then((r) => r.data),

  update: (id: string, dto: UpdateProcedureDto): Promise<ProcedureTemplate> =>
    apiClient.patch<ProcedureTemplate>(`/procedure-templates/${id}`, dto).then((r) => r.data),

  delete: (id: string): Promise<void> =>
    apiClient.delete(`/procedure-templates/${id}`).then(() => undefined),

  // Lifecycle
  publish: (id: string): Promise<ProcedureTemplate> =>
    apiClient.post<ProcedureTemplate>(`/procedure-templates/${id}/publish`).then((r) => r.data),

  archive: (id: string): Promise<ProcedureTemplate> =>
    apiClient.post<ProcedureTemplate>(`/procedure-templates/${id}/archive`).then((r) => r.data),

  newVersion: (id: string): Promise<ProcedureTemplate> =>
    apiClient.post<ProcedureTemplate>(`/procedure-templates/${id}/new-version`).then((r) => r.data),

  clone: (id: string, dto: CloneProcedureDto): Promise<ProcedureTemplate> =>
    apiClient.post<ProcedureTemplate>(`/procedure-templates/${id}/clone`, dto).then((r) => r.data),

  getVersions: (id: string): Promise<ProcedureVersion[]> =>
    apiClient.get<ProcedureVersion[]>(`/procedure-templates/${id}/versions`).then((r) => r.data),

  // Sections
  createSection: (templateId: string, dto: CreateSectionDto): Promise<ProcedureSection> =>
    apiClient
      .post<ProcedureSection>(`/procedure-templates/${templateId}/sections`, dto)
      .then((r) => r.data),

  updateSection: (
    templateId: string,
    sectionId: string,
    dto: { title: string },
  ): Promise<ProcedureSection> =>
    apiClient
      .patch<ProcedureSection>(`/procedure-templates/${templateId}/sections/${sectionId}`, dto)
      .then((r) => r.data),

  deleteSection: (templateId: string, sectionId: string): Promise<void> =>
    apiClient
      .delete(`/procedure-templates/${templateId}/sections/${sectionId}`)
      .then(() => undefined),

  reorderSections: (templateId: string, orderedIds: string[]): Promise<ProcedureSection[]> =>
    apiClient
      .patch<ProcedureSection[]>(`/procedure-templates/${templateId}/sections/reorder`, {
        orderedIds,
      })
      .then((r) => r.data),

  // Steps
  createStep: (templateId: string, sectionId: string, dto: CreateStepDto): Promise<ProcedureStep> =>
    apiClient
      .post<ProcedureStep>(`/procedure-templates/${templateId}/sections/${sectionId}/steps`, dto)
      .then((r) => r.data),

  updateStep: (
    templateId: string,
    sectionId: string,
    stepId: string,
    dto: UpdateStepDto,
  ): Promise<ProcedureStep> =>
    apiClient
      .patch<ProcedureStep>(
        `/procedure-templates/${templateId}/sections/${sectionId}/steps/${stepId}`,
        dto,
      )
      .then((r) => r.data),

  deleteStep: (templateId: string, sectionId: string, stepId: string): Promise<void> =>
    apiClient
      .delete(`/procedure-templates/${templateId}/sections/${sectionId}/steps/${stepId}`)
      .then(() => undefined),

  reorderSteps: (
    templateId: string,
    sectionId: string,
    orderedIds: string[],
  ): Promise<ProcedureStep[]> =>
    apiClient
      .patch<ProcedureStep[]>(
        `/procedure-templates/${templateId}/sections/${sectionId}/steps/reorder`,
        { orderedIds },
      )
      .then((r) => r.data),

  // Reference materials
  uploadRefMaterial: (
    templateId: string,
    sectionId: string,
    stepId: string,
    file: File,
    materialType: MaterialType,
  ): Promise<ReferenceMaterial> => {
    const form = new FormData();
    form.append('file', file);
    form.append('materialType', materialType);
    return apiClient
      .post<ReferenceMaterial>(
        `/procedure-templates/${templateId}/sections/${sectionId}/steps/${stepId}/reference-materials`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => r.data);
  },

  deleteRefMaterial: (
    templateId: string,
    sectionId: string,
    stepId: string,
    materialId: string,
  ): Promise<void> =>
    apiClient
      .delete(
        `/procedure-templates/${templateId}/sections/${sectionId}/steps/${stepId}/reference-materials/${materialId}`,
      )
      .then(() => undefined),

  quickEdit: (
    id: string,
    dto: QuickEditProcedureDto,
  ): Promise<{
    id: string;
    title: string;
    description: string | null;
    versionNumber: number;
    updatedAt: string;
  }> => apiClient.patch(`/procedure-templates/${id}/quick-edit`, dto).then((r) => r.data),
};

export interface QuickEditStepItem {
  id: string;
  title?: string;
  description?: string;
  instructionContent?: string;
}

export interface QuickEditProcedureDto {
  title?: string;
  description?: string;
  steps?: QuickEditStepItem[];
}
